<template>
  <div>
    <BaseCard v-if="quote && basePrice" mb gray v-test="'current-plan-card'">
      <BaseLabel mb v-test="`current-plan-label`">{{
        t('subscription.current_plan_label')
      }}</BaseLabel>
      <div :class="$style.planHeader">
        <BaseHeading heading="h4" :mb="0.5">
          {{ t(`subscription.types.${quote.plan.toLowerCase()}.name`) }}
        </BaseHeading>
        <div :class="$style.priceContainer">
          <BaseText bold mr>
            {{ filters.currency(basePrice.amountSubtotal, basePrice.currency) }}
            / {{ t(`global.items.${quote?.period.toLowerCase()}`) }}
          </BaseText>
          <BaseIcon
            :name="isFeaturesExpanded ? 'chevron-up' : 'chevron-down'"
            size="s"
            color="secondary"
            clickable
            :ml="0.5"
            @click="toggleFeatures"
            v-test="`toggle-features-icon-${quote.plan.toLowerCase()}`"
          />
        </div>
      </div>
      <div
        v-if="isFeaturesExpanded"
        :id="featureContainerId"
        :class="$style.featuresContainer"
      >
        <div
          v-for="featureKey in activeCurrentPlanFeatures"
          :key="featureKey"
          :class="$style.feature"
        >
          <BaseIcon name="check" color="success" size="l" :mr="0.5" />
          <BaseText>{{
            t(`subscription.stripe.features.${featureKey}`)
          }}</BaseText>
        </div>
      </div>
    </BaseCard>

    <div v-if="showOtherPlans" id="other-plans-container">
      <div v-if="isLoadingQuotes" class="flex justify-center py-4">
        <BaseSpinner />
      </div>
      <template v-else>
        <BaseCard
          v-for="plan in otherAvailablePlans"
          :key="plan.type"
          mb
          gray
          v-test="`other-plan-card-${plan.type}`"
        >
          <div :class="$style.planHeader">
            <BaseHeading heading="h4" :mb="0.5">
              {{ t(`subscription.types.${plan.type.toLowerCase()}.name`) }}
            </BaseHeading>
            <div :class="$style.priceContainer">
              <template v-if="isLoadingQuotes">
                <BaseSpinner size="s" mr />
              </template>
              <template v-else>
                <template v-if="getQuoteForPlanPeriod(plan.type)">
                  <BaseText bold mr>
                    {{
                      filters.currency(
                        getQuoteForPlanPeriod(plan.type)?.amountTotal ?? 0,
                        getQuoteForPlanPeriod(plan.type)?.lineItems?.[0]
                          ?.currency ?? ''
                      )
                    }}
                    /
                    {{
                      t(
                        `global.items.${
                          quote?.period?.toLowerCase() || 'month'
                        }`
                      )
                    }}
                  </BaseText>
                </template>
              </template>
              <BaseIcon
                :name="
                  otherPlansFeaturesExpanded[plan.type]
                    ? 'chevron-up'
                    : 'chevron-down'
                "
                size="s"
                color="secondary"
                clickable
                :ml="0.5"
                role="button"
                tabindex="0"
                @click="toggleOtherPlanFeatures(plan.type)"
                :v-test="`toggle-other-features-icon-${plan.type}`"
              />
            </div>
          </div>
          <div
            v-if="otherPlansFeaturesExpanded[plan.type]"
            :id="`other-features-${plan.type}`"
            :class="$style.featuresContainer"
          >
            <div
              v-for="featureKey in getActiveFeaturesForPlan(plan)"
              :key="featureKey"
              :class="$style.feature"
            >
              <BaseIcon name="check" color="success" size="l" :mr="0.5" />
              <BaseText>{{
                t(`subscription.stripe.features.${featureKey}`)
              }}</BaseText>
            </div>
          </div>
        </BaseCard>
      </template>
    </div>
    <BaseText
      v-if="otherAvailablePlans.length > 0"
      link
      mt
      mb
      @click="toggleOtherPlans"
      v-test="`toggle-other-plans-link`"
    >
      {{
        showOtherPlans
          ? t('subscription.hide_other_plans')
          : t('subscription.show_other_plans')
      }}
    </BaseText>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, reactive } from 'vue';
import { storeToRefs } from 'pinia';
import type { Plan, Quote, Mutation } from '@/types';
import { useMutation } from '@vue/apollo-composable';
import { STRIPE_QUOTE_MUTATION } from '@/modules/dashboard/subscription/stripe/graphql';
import { useSubscriptionStore } from '@/modules/dashboard/subscription/store';
import { plansFeaturesKeys } from '@/modules/dashboard/subscription/data/features';
import { useCurrentPlanQuote } from '../../store';

import filters from '@/filters';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const subscriptionStore = useSubscriptionStore();
const { plansData } = storeToRefs(subscriptionStore);
const currentPlanQuoteStore = useCurrentPlanQuote();
const { quote, basePrice } = storeToRefs(currentPlanQuoteStore);
const isFeaturesExpanded = ref(false);
const showOtherPlans = ref(false);
const otherPlansFeaturesExpanded = reactive<Record<string, boolean>>({});

// State for storing fetched quotes for other plans
const otherPlanQuotes = ref<Record<string, Quote[]>>({});
const isLoadingQuotes = ref(false);
const hasFetchedOtherQuotes = ref(false); // Flag to ensure fetch runs only once

onMounted(() => {
  subscriptionStore.getData();
});

const toggleOtherPlanFeatures = (planType: string): void => {
  otherPlansFeaturesExpanded[planType] = !otherPlansFeaturesExpanded[planType];
};

const toggleFeatures = (): void => {
  isFeaturesExpanded.value = !isFeaturesExpanded.value;
};

const toggleOtherPlans = (): void => {
  showOtherPlans.value = !showOtherPlans.value;

  if (showOtherPlans.value && !hasFetchedOtherQuotes.value) {
    if (otherAvailablePlans.value.length > 0) {
      fetchOtherPlanQuotes();
    }
  }
};

const fetchOtherPlanQuotes = async (): Promise<void> => {
  hasFetchedOtherQuotes.value = true; // Set flag immediately
  isLoadingQuotes.value = true;
  otherPlanQuotes.value = {}; // Clear previous quotes
  const { mutate: createQuote } = useMutation<Mutation>(STRIPE_QUOTE_MUTATION);

  try {
    for (const plan of otherAvailablePlans.value) {
      const response = await createQuote({
        input: { plan: plan.type.toUpperCase() },
      });
      const quotes = response?.data?.createStripeQuote?.quotes;
      if (quotes) {
        otherPlanQuotes.value[plan.type] = quotes;
      }
    }
  } catch (error) {
    console.error('Error fetching other plan quotes:', error);
  } finally {
    isLoadingQuotes.value = false;
  }
};

const featureContainerId = computed(
  () => `features-${Math.random().toString(36).substring(7)}`,
);

const activeCurrentPlanFeatures = computed(() => {
  const currentPlanType = quote.value?.plan?.toUpperCase();
  if (!currentPlanType || !(currentPlanType in plansFeaturesKeys)) {
    return [];
  }
  const features =
    plansFeaturesKeys[currentPlanType as keyof typeof plansFeaturesKeys];
  return [
    ...features.cash,
    ...features.scheduleManagement,
    ...features.overallTools,
    ...features.marketing,
  ];
});

const getActiveFeaturesForPlan = (plan: Plan): string[] => {
  const planType = plan.type?.toUpperCase();
  if (!planType || !(planType in plansFeaturesKeys)) {
    return [];
  }
  const features =
    plansFeaturesKeys[planType as keyof typeof plansFeaturesKeys];
  return [
    ...features.cash,
    ...features.scheduleManagement,
    ...features.overallTools,
    ...features.marketing,
  ];
};

const otherAvailablePlans = computed(() => {
  if (!quote.value?.plan || !plansData.value) {
    return [];
  }
  return plansData.value.filter(
    (plan) => plan.type.toUpperCase() !== quote.value?.plan,
  );
});

const getQuoteForPlanPeriod = (planType: string): Quote | undefined => {
  const quotesForPlan = otherPlanQuotes.value[planType];
  const currentPeriod = quote.value?.period;

  if (!quotesForPlan) {
    return undefined;
  }

  const foundQuote = quotesForPlan.find((q) => q.period === currentPeriod);

  return foundQuote;
};
</script>

<script lang="ts">
export default {
  name: 'CurrentPlanDetails'
};
</script>

<style lang="scss" module>
.planHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.priceContainer {
  display: flex;
  align-items: center;
}

.featuresContainer {
  margin-top: $spacing;
}

.feature {
  display: flex;
  align-items: center;

  &:not(:last-child) {
    margin-bottom: $spacing * 0.5;
  }
}
</style> 
